import React, { useCallback, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCoins, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import useManualCreditAthleteAppointment from 'hooks/athleteAppointment/useManualCreditAthleteAppointment';
import useManualCreditAthleteClass from 'hooks/athleteClass/useManualCreditAthleteClass';
import { TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY } from 'hooks/reports/reservations/uncredited-reservations/useTotalUncreditedReservations';
import { UNCREDITED_RESERVATIONS_QUERY_KEY } from 'hooks/reports/reservations/uncredited-reservations/useUncreditedReservations';
import { useQueryClient } from 'react-query';
import { UncreditedEvent } from 'types';
import notificationHandler from 'utils/notifications';

import CancelReservationModal from './CancelReservationModal';

interface UncreditedReservationsDetailPanelProps {
  athleteId: string;
  uncreditedReservations: UncreditedEvent[];
}

const notificationMessages = {
  credit: {
    success: {
      title: 'Reservation has been credited',
      message: 'A credit has been assigned to this reservation.',
    },
    error: {
      title: "Reservation can't be credited.",
    },
  },
};

function useHandleManualCredit(): (reservation: UncreditedEvent) => void {
  const queryClient = useQueryClient();
  const manualCreditAthleteClass = useManualCreditAthleteClass();
  const manualCreditAthleteAppointment = useManualCreditAthleteAppointment();

  const { successNotification, errorNotification } = notificationHandler();

  const handleManualCredit = useCallback(
    (reservation: UncreditedEvent): void => {
      const { type, athleteEventId } = reservation;

      if (type === 'Class') {
        manualCreditAthleteClass.mutate(
          {
            athleteKiloClassId: athleteEventId,
            manualCredit: { action: 'credit' },
          },
          {
            onSuccess: () => {
              successNotification(
                notificationMessages.credit.success.title,
                notificationMessages.credit.success.message,
              );
              queryClient.invalidateQueries([UNCREDITED_RESERVATIONS_QUERY_KEY]);
              queryClient.invalidateQueries([TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY]);
            },
            onError: error => {
              errorNotification(notificationMessages.credit.error.title, error.response?.data.message);
            },
          },
        );
      }

      if (type === 'Appointment') {
        manualCreditAthleteAppointment.mutate(
          {
            athleteAppointmentId: athleteEventId,
            manualCredit: { action: 'credit' },
          },
          {
            onSuccess: () => {
              successNotification(
                notificationMessages.credit.success.title,
                notificationMessages.credit.success.message,
              );
              queryClient.invalidateQueries([UNCREDITED_RESERVATIONS_QUERY_KEY]);
              queryClient.invalidateQueries([TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY]);
            },
            onError: error => {
              errorNotification(notificationMessages.credit.error.title, error.response?.data.message);
            },
          },
        );
      }
    },
    [
      queryClient,
      manualCreditAthleteAppointment,
      manualCreditAthleteClass,
      errorNotification,
      successNotification,
    ],
  );

  return handleManualCredit;
}

const UncreditedReservationsDetailPanel = ({
  uncreditedReservations,
}: UncreditedReservationsDetailPanelProps): JSX.Element => {
  const theme = useMantineTheme();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedReservation, setSelectedReservation] = useState<UncreditedEvent | null>(null);

  const handleManualCredit = useHandleManualCredit();

  const handleOpenDeleteModal = (reservation: UncreditedEvent): void => {
    setSelectedReservation(reservation);
    open();
  };

  const handleCloseDeleteModal = (): void => {
    close();
    setSelectedReservation(null);
  };

  const handleCreditClick = (reservation: UncreditedEvent): void => {
    handleManualCredit(reservation);
  };

  if (uncreditedReservations.length === 0) {
    return (
      <Box p='md'>
        <Text c='dimmed' fs='italic' ta='center'>
          No uncredited reservations found
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box p='md'>
        <Flex gap='xl' justify='center' align='flex-start' direction='row' wrap='nowrap' m={0} bg='gray.1'>
          <Paper shadow='sm' radius='md' withBorder sx={{ flexBasis: '60%' }}>
            <Stack spacing='xs' py='sm'>
              {/* Header */}
              <Stack spacing={3}>
                <Group grow noWrap px='md' ta='center' sx={{ whiteSpace: 'nowrap' }}>
                  <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                    Date & Time
                  </Text>
                  <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                    Event Type
                  </Text>
                  <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                    Coach(es)
                  </Text>
                  <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                    Actions
                  </Text>
                </Group>
                <Divider />
              </Stack>

              {/* Rows */}
              {uncreditedReservations.map(reservation => (
                <Group
                  key={reservation.athleteEventId}
                  grow
                  noWrap
                  px='md'
                  ta='center'
                  py='xs'
                  sx={{
                    borderLeft: `4px solid ${
                      reservation.eventTypeColor ? reservation.eventTypeColor : theme.colors.gray[0]
                    }`,
                    '&:hover': {
                      backgroundColor: theme.colors.gray[0],
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                  data-cy={`uncredited-row-${reservation.athleteEventId}`}
                >
                  <Stack spacing={1}>
                    <Text size='sm' fw={500} data-cy='startDate'>
                      {dayjs(reservation.startDate).format('MMM D, YYYY')}
                    </Text>

                    <Text size='sm' fw={500} data-cy='startTime' color='dimmed'>
                      {reservation.startTime}
                    </Text>
                  </Stack>

                  <Group position='center' spacing='xs'>
                    <Badge
                      variant='filled'
                      size='sm'
                      fw='bold'
                      data-cy='eventTypeName'
                      bg={reservation.eventTypeColor ? reservation.eventTypeColor : theme.colors.gray[0]}
                    >
                      {reservation.eventTypeName}
                    </Badge>
                  </Group>

                  {reservation.coaches.length > 0 ? (
                    <Group position='center' spacing='xs'>
                      <Text size='sm' fw={500} data-cy='coaches' truncate>
                        {reservation.coaches.map(coach => coach.fullName).join(', ')}
                      </Text>
                    </Group>
                  ) : (
                    <Text size='sm' c='dimmed'>
                      -
                    </Text>
                  )}

                  <Group spacing={5} position='center'>
                    <Tooltip label='Assign credit'>
                      <ActionIcon onClick={() => handleCreditClick(reservation)}>
                        <IconCoins size='1.125rem' />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label='Cancel reservation'>
                      <ActionIcon color='red' onClick={() => handleOpenDeleteModal(reservation)}>
                        <IconTrash size='1.125rem' />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              ))}
            </Stack>
          </Paper>
        </Flex>
      </Box>

      <CancelReservationModal
        opened={opened}
        onClose={handleCloseDeleteModal}
        reservation={selectedReservation}
      />
    </>
  );
};

export default UncreditedReservationsDetailPanel;
